import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface FragmentParams {
  copies: number;
  offset: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Fragment: Filter<FragmentParams> = {
  id: "fragment",
  displayName: "Fragment",
  defaultParams: { copies: 4, offset: 3 },

  apply(input: PixelBuffer, params: FragmentParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const copies = Math.max(2, Math.round(params.copies));
    const offset = params.offset;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;

        for (let c = 0; c < copies; c++) {
          const angle = (c / copies) * Math.PI * 2;
          const dx = Math.round(Math.cos(angle) * offset);
          const dy = Math.round(Math.sin(angle) * offset);
          const sx = x + dx, sy = y + dy;
          if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
            const si = (sy * w + sx) * 4;
            sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!; sumA += src[si + 3]!;
            count++;
          }
        }

        const o = (y * w + x) * 4;
        if (count > 0) {
          dst[o] = clamp8(Math.round(sumR / count));
          dst[o + 1] = clamp8(Math.round(sumG / count));
          dst[o + 2] = clamp8(Math.round(sumB / count));
          dst[o + 3] = clamp8(Math.round(sumA / count));
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
